import { ForwardedRef, forwardRef } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import { observer } from 'mobx-react';
import cls from 'classnames';
import AutoLinks from 'quill-auto-links';

import styles from './RichEditor.module.css';

Quill.register('modules/autoLinks', AutoLinks);

export enum RichEditorTheme {
  Dark = 'dark',
  White = 'white',
}

export interface RichEditorProps extends Omit<ReactQuill.ReactQuillProps, 'theme' | 'readOnly' | 'value'> {
  value?: Nullable<string>;
  theme?: RichEditorTheme;
  hideToolbar?: boolean;
  className?: string;
  disabled?: boolean;
}

function RichEditor(props: RichEditorProps, ref: ForwardedRef<ReactQuill>) {
  const { theme = RichEditorTheme.Dark, hideToolbar, disabled = false, className, value, ...rest } = props;

  return (
    <ReactQuill
      ref={ref}
      formats={['bold', 'italic', 'underline', 'strike', 'list', 'blockquote', 'link']}
      modules={{
        autoLinks: true,
        toolbar: hideToolbar
          ? null
          : [['bold', 'italic', 'link', 'blockquote'], [{ list: 'ordered' }, { list: 'bullet' }], ['clean']],
        clipboard: {
          // without this option editor add newLine to ul lists.
          // This is a known bug of react-quill - https://github.com/zenoamaro/react-quill/issues/925
          matchVisual: false,
        },
      }}
      className={cls(
        { [styles.darkTheme]: theme === RichEditorTheme.Dark, [styles.withToolbar]: !hideToolbar },
        className,
      )}
      theme="snow"
      readOnly={disabled}
      {...rest}
      value={value ?? undefined}
    />
  );
}

export default observer(forwardRef(RichEditor));
